import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ApiServices from '../Services/ApiServices';
import { toastHelper } from '../../Helpers/toastHelper';
import Loader from '../Shared/Loader';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTag, faUser, faNoteSticky, faSave, faArrowRight, faSearch } from '@fortawesome/free-solid-svg-icons';
import Select from 'react-select';
import { useFormik } from 'formik';
import * as Yup from 'yup';

const AddSubscription = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [teachers, setTeachers] = useState([]);
    const [plans, setPlans] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');

    const validationSchema = Yup.object({
        user_id: Yup.string().required('يرجى اختيار المعلم'),
        plan_id: Yup.string().required('يرجى اختيار الباقة'),
        notes: Yup.string().max(500, 'الملاحظات يجب أن لا تتجاوز 500 حرف')
    });

    const formik = useFormik({
        initialValues: {
            user_id: '',
            plan_id: '',
            notes: ''
        },
        validationSchema,
        onSubmit: async (values) => {
            setSubmitting(true);
            try {
                const response = await ApiServices.post('Subscription/User/create', values);
                toastHelper.success('تم إنشاء الاشتراك بنجاح');
                navigate('/admin/dashboard');
            } catch (error) {
                toastHelper.error(error.message || error.msg || 'فشل في إنشاء الاشتراك');

            } finally {
                setSubmitting(false);
            }
        }
    });

    const customSelectStyles = {
        container: (base) => ({
            ...base,
            width: '100%',
        }),
        control: (base, state) => ({
            ...base,
            padding: '6px',
            borderRadius: '12px',
            border: state.isFocused ? '2px solid #6366f1' : '2px solid #e2e8f0',
            boxShadow: state.isFocused ? '0 0 0 4px rgba(99, 102, 241, 0.1)' : 'none',
            '&:hover': {
                borderColor: state.isFocused ? '#6366f1' : '#cbd5e1'
            },
            fontFamily: 'Zain, sans-serif'
        }),
        option: (base, state) => ({
            ...base,
            backgroundColor: state.isSelected ? '#6366f1' : state.isFocused ? '#f1f5f9' : 'white',
            color: state.isSelected ? 'white' : '#334155',
            '&:active': {
                backgroundColor: '#6366f1'
            },
            fontFamily: 'Zain, sans-serif'
        }),
        placeholder: (base) => ({
            ...base,
            fontFamily: 'Zain, sans-serif',
            color: '#94a3b8'
        }),
        singleValue: (base) => ({
            ...base,
            fontFamily: 'Zain, sans-serif',
            color: '#1e293b'
        })
    };

    const fetchData = async () => {
        try {
            setLoading(true);
            const [teachersRes, plansRes] = await Promise.all([
                ApiServices.get('Lookup/Teachers'),
                ApiServices.get('Lookup/Plans')
            ]);

            if (Array.isArray(teachersRes)) setTeachers(teachersRes);
            if (Array.isArray(plansRes)) setPlans(plansRes);
        } catch (error) {
            toastHelper.error('فشل في تحميل البيانات الأساسية');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleSearchTeachers = async (inputValue) => {
        if (!inputValue) return;

        try {
            const response = await ApiServices.get(`Lookup/Teachers/filter?Name=${encodeURIComponent(inputValue)}`);
            if (Array.isArray(response)) {
                setTeachers(response);
                return response.map(t => ({ value: t.id, label: t.name }));
            }
        } catch (error) {
            
        }
        return [];
    };


    if (loading) return <Loader text="جاري تحميل الخيارات..." />;

    return (
        <div className="add-sub-page">
            <div className="form-container">
                <header className="form-header">
                    <button className="back-btn" onClick={() => navigate(-1)}>
                        <FontAwesomeIcon icon={faArrowRight} />
                        <span>عودة</span>
                    </button>
                    <h1>إضافة اشتراك جديد</h1>
                </header>

                <form onSubmit={formik.handleSubmit}>
                    <div className="form-grid">
                        <div className="input-group full-width">
                            <label>
                                <FontAwesomeIcon icon={faUser} />
                                المعلم المستفيد
                            </label>
                            <Select
                                options={teachers.map(t => ({ value: t.id, label: t.name }))}
                                placeholder="ابحث واختر المعلم..."
                                isRtl={true}
                                styles={customSelectStyles}
                                className={formik.touched.user_id && formik.errors.user_id ? 'select-error' : ''}
                                onChange={(opt) => formik.setFieldValue('user_id', opt ? opt.value : '')}
                                onInputChange={(val) => {
                                    if (val.length > 2) handleSearchTeachers(val);
                                }}
                            />
                            {formik.touched.user_id && formik.errors.user_id && (
                                <div className="field-error">{formik.errors.user_id}</div>
                            )}
                        </div>

                        <div className="input-group full-width">
                            <label>
                                <FontAwesomeIcon icon={faTag} />
                                باقة الاشتراك
                            </label>
                            <Select
                                options={plans.map(p => ({ value: p.id, label: p.name }))}
                                placeholder="اختر الباقة..."
                                isRtl={true}
                                styles={customSelectStyles}
                                className={formik.touched.plan_id && formik.errors.plan_id ? 'select-error' : ''}
                                onChange={(opt) => formik.setFieldValue('plan_id', opt ? opt.value : '')}
                            />
                            {formik.touched.plan_id && formik.errors.plan_id && (
                                <div className="field-error">{formik.errors.plan_id}</div>
                            )}
                        </div>

                        <div className="input-group full-width">
                            <label>
                                <FontAwesomeIcon icon={faNoteSticky} />
                                ملاحظات إضافية
                            </label>
                            <textarea
                                {...formik.getFieldProps('notes')}
                                placeholder="أي تفاصيل أو ملاحظات حول هذا الاشتراك..."
                                rows="4"
                                className={formik.touched.notes && formik.errors.notes ? 'input-error' : ''}
                            />
                            {formik.touched.notes && formik.errors.notes && (
                                <div className="field-error">{formik.errors.notes}</div>
                            )}
                        </div>
                    </div>

                    <footer className="form-footer">
                        <button
                            type="submit"
                            className="submit-btn"
                            disabled={submitting}
                        >
                            {submitting ? 'جاري الاشتراك...' : (
                                <>
                                    <FontAwesomeIcon icon={faSave} />
                                    <span>إنشاء الاشتراك</span>
                                </>
                            )}
                        </button>
                    </footer>
                </form>
            </div>

            <style>{`
                .add-sub-page {
                    padding: 40px 20px;
                    background: #f8fafc;
                    min-height: 100vh;
                    direction: rtl;
                      font-family: "Zain", sans-serif;
                }
                .form-container {
                    max-width: 800px;
                    margin: 0 auto;
                    background: white;
                    padding: 40px;
                    border-radius: 24px;
                    box-shadow: 0 10px 40px rgba(0,0,0,0.05);
                }
                .form-header {
                    display: flex;
                    align-items: center;
                    gap: 20px;
                    margin-bottom: 40px;
                    border-bottom: 2px solid #f1f5f9;
                    padding-bottom: 20px;
                }
                .back-btn {
                    background: #f1f5f9;
                    border: none;
                    padding: 10px 20px;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    cursor: pointer;
                    color: #64748b;
                    font-weight: 600;
                    transition: all 0.2s;
                }
                .back-btn:hover {
                    background: #e2e8f0;
                    color: #1e293b;
                }
                .form-header h1 {
                    margin: 0;
                    font-size: 1.8rem;
                    color: #1e293b;
                    font-weight: 800;
                }
                .form-grid {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 30px;
                }
                .input-group {
                    position: relative;
                }
                .full-width {
                    grid-column: 1 / -1;
                }
                .input-group label {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    margin-bottom: 12px;
                    font-weight: 700;
                    color: #475569;
                    font-size: 0.95rem;
                }
                .input-group textarea {
                    width: 100%;
                    padding: 14px 18px;
                    border: 2px solid #e2e8f0;
                    border-radius: 12px;
                    font-size: 1rem;
                    transition: all 0.2s;
                      font-family: "Zain", sans-serif;
                    background: #f8fafc;
                    display: block;
                }
                .input-group textarea:focus {
                    border-color: #6366f1;
                    background: white;
                    outline: none;
                    box-shadow: 0 0 0 4px rgba(99, 102, 241, 0.1);
                }
                .input-group .input-error {
                    border-color: #ef4444;
                }
                .field-error {
                    color: #ef4444;
                    font-size: 0.85rem;
                    margin-top: 8px;
                    font-weight: 700;
                    display: flex;
                    align-items: center;
                    gap: 5px;
                }
                .field-error::before {
                    content: '●';
                    font-size: 0.6rem;
                }

                .form-footer {
                    margin-top: 40px;
                    padding-top: 30px;
                    border-top: 2px solid #f1f5f9;
                    display: flex;
                    justify-content: center;
                }
                .submit-btn {
                    background: linear-gradient(135deg, #6366f1 0%, #4f46e5 100%);
                    color: white;
                    border: none;
                    padding: 16px 50px;
                    border-radius: 15px;
                    font-size: 1.1rem;
                    font-weight: 700;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                    box-shadow: 0 10px 20px -5px rgba(99, 102, 241, 0.4);
                }
                .submit-btn:hover:not(:disabled) {
                    transform: translateY(-2px);
                    box-shadow: 0 15px 30px -5px rgba(99, 102, 241, 0.5);
                }
                .submit-btn:disabled {
                    opacity: 0.7;
                    cursor: not-allowed;
                }

                @media (max-width: 640px) {
                    .form-container {
                        padding: 24px;
                    }
                    .back-btn span {
                        display: none;
                    }
                }
            `}</style>
        </div>
    );
};

export default AddSubscription;
